import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiDownload, FiLoader, FiCheck, FiMail } = FiIcons

const Download = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [checkingStatus, setCheckingStatus] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Invalid download link. Please check your email for the correct link.')
      setLoading(false)
      return
    }

    const checkStatus = async () => {
      try {
        // Get session by token
        const response = await fetch(`/api/download/get-session?token=${token}`)
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Invalid or expired download link. Please contact support if you believe this is an error.')
          } else {
            setError('Error loading your files. Please try again.')
          }
          setLoading(false)
          return
        }

        const data = await response.json()
        setSession(data)

        // If still processing, check fal.ai status
        if (data.status === 'processing') {
          setCheckingStatus(true)
          
          try {
            const checkResponse = await fetch('/api/check-fal-status', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId: data.id })
            })
            
            if (checkResponse.ok) {
              const checkData = await checkResponse.json()
              if (checkData.allComplete) {
                // Refresh session data after a short delay
                setTimeout(() => {
                  window.location.reload()
                }, 2000)
              }
            }
          } catch (checkError) {
            console.error('Error checking fal.ai status:', checkError)
            // Don't show error to user, just continue polling
          } finally {
            setCheckingStatus(false)
          }
        }

        setLoading(false)
      } catch (err) {
        console.error('Error checking status:', err)
        setError('Error loading your files. Please try again.')
        setLoading(false)
      }
    }

    checkStatus()
    
    // Auto-refresh every 15 seconds if still processing
    const interval = setInterval(() => {
      if (session?.status === 'processing') {
        checkStatus()
      }
    }, 15000)

    return () => clearInterval(interval)
  }, [token, session?.status])

  const downloadFile = async (url, filename) => {
    try {
      if (!url || url === '') {
        alert('Error: Invalid file URL. Please try again.')
        return
      }

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading file:', error)
      alert(`Error downloading ${filename}. Please try again.`)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <SafeIcon icon={FiLoader} className="text-6xl text-primary-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Loading...</h2>
          <p className="text-gray-600">Please wait while we load your files.</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
          >
            Return Home
          </button>
        </div>
      </div>
    )
  }

  if (!session) {
    return null
  }

  // Still processing
  if (session.status === 'processing') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <SafeIcon icon={FiLoader} className="text-6xl text-primary-600 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Processing Your Signature...</h2>
          <p className="text-gray-600 mb-4">
            Your signature is being processed. This page will automatically update when your files are ready.
          </p>
          {checkingStatus && (
            <p className="text-sm text-gray-500 mb-4">Checking processing status...</p>
          )}
          <p className="text-xs text-gray-500 mt-4">
            This usually takes 2-5 minutes. You can close this page and return later using the same link from your email.
          </p>
        </div>
      </div>
    )
  }

  // Completed - show download buttons
  if (session.status === 'completed' && session.final_jpg_url && session.final_png_url && session.final_svg_url) {
    return (
      <div className="min-h-screen bg-white py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <SafeIcon icon={FiCheck} className="text-6xl text-green-500 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Your Signature is Ready!</h1>
            <p className="text-xl text-gray-600 mb-2">Thank you, {session.name}!</p>
            <p className="text-gray-500">Download your professional signature in all formats below.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* JPG Download */}
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-primary-500 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">JPG Format</h3>
                <p className="text-sm text-gray-600 mb-4">High-quality image with black background</p>
                <button
                  onClick={() => downloadFile(session.final_jpg_url, `${session.name}-signature.jpg`)}
                  className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiDownload} className="text-xl" />
                  Download JPG
                </button>
              </div>
            </div>

            {/* PNG Download */}
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-primary-500 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">PNG Format</h3>
                <p className="text-sm text-gray-600 mb-4">Transparent background for any use</p>
                <button
                  onClick={() => downloadFile(session.final_png_url, `${session.name}-signature.png`)}
                  className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiDownload} className="text-xl" />
                  Download PNG
                </button>
              </div>
            </div>

            {/* SVG Download */}
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-primary-500 transition-colors">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">SVG Format</h3>
                <p className="text-sm text-gray-600 mb-4">Scalable vector format</p>
                <button
                  onClick={() => downloadFile(session.final_svg_url, `${session.name}-signature.svg`)}
                  className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiDownload} className="text-xl" />
                  Download SVG
                </button>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate('/')}
              className="text-primary-600 hover:text-primary-700 underline"
            >
              Create Another Signature
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Processing Error</h2>
        <p className="text-gray-600 mb-4">
          There was an error processing your signature. Please contact support for assistance.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
        >
          Return Home
        </button>
      </div>
    </div>
  )
}

export default Download
